import React from 'react';
import { Box, Button, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EngineeringIcon from '@mui/icons-material/Engineering';
import StorefrontIcon from '@mui/icons-material/Storefront';

const FounderPhotoCard = ({ compact = false }) => {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: compact ? 1.75 : 2 },
        borderRadius: 4,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 28px 60px -36px rgba(13, 71, 161, 0.45)',
        maxWidth: compact ? 340 : 420,
        mx: { xs: 'auto', md: 0 },
      }}
    >
      <Box
        component="img"
        src="/about/selahattin-demir.jpg"
        alt={t('founder.name')}
        loading="lazy"
        sx={{
          display: 'block',
          width: '100%',
          aspectRatio: compact ? '4 / 3' : '3 / 3.35',
          objectFit: 'cover',
          objectPosition: compact ? 'center bottom' : 'center center',
          borderRadius: 3,
          bgcolor: 'background.default',
        }}
      />
      <Box sx={{ px: 1, pt: 1.5, pb: 0.75, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.35 }}>
          {t('founder.caption')}
        </Typography>
      </Box>
    </Paper>
  );
};

const FounderSection = ({ variant = 'full' }) => {
  const { t } = useTranslation();
  const compact = variant === 'compact';

  const pillars = [
    { icon: EngineeringIcon, label: t('founder.pillars.technical') },
    { icon: StorefrontIcon, label: t('founder.pillars.trade') },
    { icon: HandshakeIcon, label: t('founder.pillars.trust') },
  ];

  return (
    <Box
      id={compact ? undefined : 'kurucumuz'}
      sx={{
        py: { xs: compact ? 6 : 7, md: compact ? 8 : 11 },
        bgcolor: compact ? 'background.default' : 'background.paper',
        scrollMarginTop: { xs: 88, md: 104 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: compact ? 5 : 7 }} alignItems="center">
          <Grid item xs={12} md={compact ? 4 : 5}>
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55 }}
              viewport={{ once: true }}
            >
              <FounderPhotoCard compact={compact} />
            </motion.div>
          </Grid>

          <Grid item xs={12} md={compact ? 8 : 7}>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55 }}
              viewport={{ once: true }}
            >
              <Typography variant="overline" sx={{ color: 'primary.main', mb: 1.5, display: 'block' }}>
                {compact ? t('founder.homeEyebrow') : t('founder.eyebrow')}
              </Typography>
              <Typography variant={compact ? 'h3' : 'h2'} sx={{ color: 'text.primary', mb: 2 }}>
                {compact ? t('founder.homeTitle') : t('founder.title')}
              </Typography>
              {!compact && (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '1rem', md: '1.08rem' },
                    lineHeight: 1.8,
                    mb: 3,
                    maxWidth: 760,
                  }}
                >
                  {t('founder.subtitle')}
                </Typography>
              )}

              <Stack spacing={compact ? 2.25 : 2.5} sx={{ mb: compact ? 3.5 : 4 }}>
                {(compact ? [t('founder.homeText')] : t('founder.paragraphs', { returnObjects: true })).map((paragraph, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.85,
                      fontSize: compact ? '1rem' : { xs: '1rem', md: '1.05rem' },
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Stack>

              {!compact && (
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  {pillars.map((pillar) => (
                    <Grid item xs={12} sm={4} key={pillar.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          p: 1.5,
                          borderRadius: 3,
                          bgcolor: 'rgba(21,101,192,0.06)',
                          border: '1px solid rgba(21,101,192,0.14)',
                          height: '100%',
                        }}
                      >
                        <pillar.icon sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
                        <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: '0.92rem' }}>
                          {pillar.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}

              {compact && (
                <Button
                  component={Link}
                  to="/hakkimizda#kurucumuz"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                >
                  {t('founder.button')}
                </Button>
              )}
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FounderSection;
